import {useEffect, useState, useCallback} from "react";
import {Button, Icon, Text} from "@reside/ui";
import {WarningContainer} from "./styles";
import {useAutoLogout} from "../../contexts/AutoLogoutContext";
import {http} from "../../services/api/restApiClient";

/**
 * Displays countdown warning before auto-logout on authenticated pages
 * Shows 5 seconds before timeout with countdown timer and "Stay logged in" button
 * Used on both Admin and RE authenticated routes (wrapped by AutoLogoutProvider)
 * Does NOT appear on login pages - only when actively using the app
 */
export const AutoLogoutWarningBar = (): JSX.Element | null => {
  const {isWarning, getRemainingTime, resetTimeout} = useAutoLogout();
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!isWarning) {
      setSeconds(0);
      return () => {};
    }

    // Poll remaining time every second
    const updateSeconds = () => {
      const remaining = Math.ceil(getRemainingTime() / 1000);
      setSeconds(remaining);
    };

    updateSeconds(); // Initial update
    const interval = setInterval(updateSeconds, 1000);

    return () => clearInterval(interval);
  }, [isWarning, getRemainingTime]);

  // Refresh token when user clicks "Stay logged in"
  // This ensures token is refreshed immediately when user explicitly wants to stay logged in
  const handleStayLoggedIn = useCallback(async () => {
    // Reset the inactivity timeout first
    resetTimeout();

    // Then refresh the token to extend session
    // The responseTransform will automatically extract X-Refreshed-JWT header
    // and update the token in Redux store
    try {
      await http.get("/auth/refresh");
    } catch (error) {
      // Error handling is done by responseTransform
      // If token is expired, it will trigger logout
    }
  }, [resetTimeout]);

  if (!isWarning) {
    return null;
  }

  // Show countdown (avoid 0 so bar stays visible; getRemainingTime can briefly be 0 on first frame)
  const displaySeconds = Math.max(1, seconds);

  return (
    <WarningContainer>
      <Icon name="info" />
      <Text>
        Your session will expire in 00:
        {displaySeconds < 10 ? `0${displaySeconds}` : displaySeconds} seconds for security purposes.
        Click &quot;Stay logged in&quot; to remain signed in.
      </Text>
      <Button color="neutral" onClick={handleStayLoggedIn}>
        Stay logged in
      </Button>
    </WarningContainer>
  );
};
